import type { BadgeTone } from '@/shared/components/ui/Badge';
import { consoleAuthorityOf } from '@/shared/permissions/administration';
import { tierOf, type AuthorityTier } from '@/shared/permissions/identity';
import type { LeaveStatus } from './types';

/**
 * Authority-tier presentation for the Team & Access directory. Attribution is
 * resolved by the shared Executive Authority Model; no local role-label map.
 */
export const AUTHORITY_TIER_TONE: Record<AuthorityTier, BadgeTone> = {
  SUPER_ADMIN: 'primary',
  ADMIN: 'info',
  EMPLOYEE: 'neutral',
};

/** Tier-safe attribution for a backend role (never exposes the raw role). */
export function authorityLabel(role?: string | null, displayName?: string | null): string {
  return consoleAuthorityOf({ role, displayName }).attribution;
}

export function authorityTone(role?: string | null): BadgeTone {
  return AUTHORITY_TIER_TONE[tierOf(role)];
}

// Legacy leave type options — these serve as fallback if dynamic types haven't loaded.
export const LEAVE_TYPE_OPTIONS = [
  { value: 'annual', label: 'Annual' },
  { value: 'sick', label: 'Sick' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'other', label: 'Other' },
];

export const LEAVE_TYPE_LABELS: Record<string, string> = {
  annual: 'Annual',
  sick: 'Sick',
  emergency: 'Emergency',
  unpaid: 'Unpaid',
  other: 'Other',
};

export const LEAVE_STATUS_TONE: Record<LeaveStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
};

export const LEAVE_STATUS_LABEL: Record<LeaveStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  it: 'IT',
  digital_marketing: 'Digital Mktg',
  accounts: 'Accounts',
  management: 'Management',
};

/**
 * Maps an onboarding candidate's companyDocuments[].code to the backend
 * route slug that renders its printable HTML (letterhead-wrapped). Only
 * documents with a generated template have a slug here — relieving_letter
 * (an offboarding document, not part of the onboarding bundle) doesn't have
 * a viewer entry point yet even though its render route exists.
 */
export const COMPANY_DOC_SLUGS: Record<string, string> = {
  offer_letter: 'offer-letter',
  appointment_letter: 'appointment-letter',
  enrolment_form: 'enrolment-form',
  induction: 'induction-form',
  code_of_conduct: 'code-of-conduct',
  nda: 'nda',
};

export function fmtDate(d?: string): string {
  if (!d) return '—';
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
